import AddTestStepper from "../../components/add/AddTestStepper";
import AddTestPageNavButtons from "./AddTestPageNavButtons";
import VideoCard from "../global/VideoCard";
import { TestCard } from "../global/VideoCard";
import React, { useContext } from "react";
import { VideoContext } from "../../context/video_context";
import { LargeThumbnail } from "./Thumbnail";
import moment from "moment";
import { toast } from "sonner";
import TestOptions from "../global/TestOptions";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../ui/accordion";

function getNextPacificMidnight(): Date {
  const now = new Date();
  const todayPacific = now.toLocaleDateString("en-CA", { timeZone: "America/Los_Angeles" });
  const [y, m, d] = todayPacific.split("-").map(Number);
  const noonUTC = new Date(Date.UTC(y, m - 1, d + 1, 12, 0, 0));
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Los_Angeles",
    hour: "numeric",
    minute: "numeric",
    hour12: false,
  }).formatToParts(noonUTC);
  const pacificHour = parseInt(parts.find((p) => p.type === "hour")?.value ?? "7");
  const pacificMinute = parseInt(parts.find((p) => p.type === "minute")?.value ?? "0");
  return new Date(noonUTC.getTime() - (pacificHour * 60 + pacificMinute) * 60000);
}

type SelectVideos = {
  handlePageChangeUp: any;
  handlePageChangeDown: any;
  handlePageChange: any;
};

const activeStep: number = 2;

export default function TestSettings(fn: SelectVideos) {
  const videoContext = useContext(VideoContext);
  const selectedVideo = videoContext?.selectedVideo;
  const files = videoContext?.files || [];
  const tests = videoContext?.tests || [];
  const loading = videoContext?.loading || false;
  const duration = videoContext?.duration || 0;
  const createTest = videoContext?.createTest || (() => Promise.resolve({}));

  return (
    <div className="w-full py-4 flex flex-col h-full">
      <AddTestStepper activeStep={activeStep} />
      <div className="flex flex-col md:flex-row py-4">
        <div className="px-6 py-4 flex-grow">
          <p className="text-base font-medium text-foreground/80 pb-4">
            {(() => {
              const startDate = getNextPacificMidnight();
              const endDate = new Date(startDate.getTime() + tests.length * duration * 1000);
              return (
                <>
                  Running {tests.length} {tests.length === 1 ? "test" : "tests"} starting{" "}
                  <span
                    className="text-primary font-semibold"
                    title="Tests always begin at midnight Pacific Time to align with YouTube Analytics daily data"
                  >
                    {moment(startDate).format("h:mm a, ddd MMM Do")} (midnight Pacific)
                  </span>
                  , ending{" "}
                  <span className="font-semibold">
                    {moment(endDate).format("ddd, MMM Do YYYY")}
                  </span>
                </>
              );
            })()}
          </p>

          <Accordion type="single" collapsible className="mb-4 border border-border rounded-lg px-4">
            <AccordionItem value="advanced" className="border-0">
              <AccordionTrigger className="text-sm font-medium">
                Advanced Settings
              </AccordionTrigger>
              <AccordionContent>
                <TestOptions />
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-4 py-4">
            {tests.map((test: any) => (
              <div key={test.video_id} className="min-w-[326px]">
                <TestCard {...test} />
              </div>
            ))}
          </div>
        </div>
        <div className="ml-auto p-6">
          <VideoCard
            handlePageChangeUp={fn.handlePageChangeUp}
            views={selectedVideo?.views || "0"}
            uploaded={selectedVideo?.uploaded || new Date()}
            video_id={selectedVideo?.video_id || ""}
            title={selectedVideo?.title || ""}
            imgLink={selectedVideo?.imgLink || ""}
          />
          <h4 className="text-base font-semibold py-4">Thumbnails</h4>
          <div className="flex flex-col gap-4">
            {files.map((file: any, index: number) => (
              <LargeThumbnail key={index} image={URL.createObjectURL(file)} />
            ))}
          </div>
        </div>
      </div>
      <AddTestPageNavButtons
        step={activeStep}
        nextTitle="Deploy"
        disabled={loading}
        handlePageChangeUp={() => {
          toast.promise(
            createTest().then(() => {
              fn.handlePageChange(0);
            }),
            {
              loading: "Creating Test...",
              success: "Test Created!",
              error: "Error creating test",
            }
          );
        }}
        handlePageChangeDown={fn.handlePageChangeDown}
      />
    </div>
  );
}
